import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    Chip,
    Autocomplete,
    FormHelperText,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Delete, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import './SeriesForm.css';
import { useTemplates } from '../../hooks/useTemplates';

const SeriesForm = ({ open, onClose, onSubmit, initialData, availableVideos = [] }) => {
    const { templates, fetchTemplates } = useTemplates();
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        description: '',
        thumbnailUrl: '',
        templateId: '',
        additionalInfo: '',
        userAccess: [],
        episodes: []
    });
    const [contentSearch, setContentSearch] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            fetchTemplates();
        }
    }, [open, fetchTemplates]);

    useEffect(() => {
        if (initialData) {
            let episodes = [];
            if (Array.isArray(initialData.episodes)) {
                if (initialData.episodes.length > 0 && initialData.episodes[0].episode_content) {
                    episodes = initialData.episodes.map(ep => ({
                        content_id: ep.episode_content.content_id || ep.episode_content.id,
                        index: ep.episode_index
                    }));
                } else {
                    episodes = [...initialData.episodes];
                }
            }
            episodes.sort((a, b) => (a.index || 0) - (b.index || 0));
            episodes = episodes.map((ep, i) => ({ ...ep, index: i + 1 }));
            setFormData({
                title: initialData.title || '',
                subtitle: initialData.subtitle || '',
                description: initialData.description || '',
                thumbnailUrl: initialData.thumbnailUrl || '',
                templateId: initialData.templateId || initialData.serie_template_id || '',
                additionalInfo: initialData.additionalInfo || '',
                userAccess: initialData.userAccess || initialData.serie_access || [],
                episodes
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleVideoClick = (content) => {
        const existingEpisodeIndex = formData.episodes.findIndex(ep => ep.content_id === content.id);

        if (existingEpisodeIndex !== -1) {
            // Remove episode if already selected
            setFormData(prev => ({
                ...prev,
                episodes: prev.episodes.filter((_, index) => index !== existingEpisodeIndex)
            }));
        } else {
            // Add new episode with next index
            const nextIndex = formData.episodes.length + 1;
            setFormData(prev => ({
                ...prev,
                episodes: [...prev.episodes, {
                    content_id: content.id,
                    index: nextIndex
                }]
            }));
        }
        // Clear episodes error if any
        if (errors.episodes) {
            setErrors(prev => ({ ...prev, episodes: '' }));
        }
    };

    const moveEpisodeUp = (index) => {
        if (index === 0) return;
        setFormData(prev => {
            const newEpisodes = [...prev.episodes];
            const temp = newEpisodes[index];
            newEpisodes[index] = newEpisodes[index - 1];
            newEpisodes[index - 1] = temp;
            // Atualizar índices
            newEpisodes.forEach((ep, i) => { ep.index = i + 1; });
            return { ...prev, episodes: newEpisodes };
        });
    };

    const moveEpisodeDown = (index) => {
        setFormData(prev => {
            if (index === prev.episodes.length - 1) return prev;
            const newEpisodes = [...prev.episodes];
            const temp = newEpisodes[index];
            newEpisodes[index] = newEpisodes[index + 1];
            newEpisodes[index + 1] = temp;
            // Atualizar índices
            newEpisodes.forEach((ep, i) => { ep.index = i + 1; });
            return { ...prev, episodes: newEpisodes };
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Título é obrigatório';
        if (!formData.description) newErrors.description = 'Descrição é obrigatória';
        if (!formData.thumbnailUrl) newErrors.thumbnailUrl = 'URL da thumbnail é obrigatória';
        if (!formData.templateId) newErrors.templateId = 'Template é obrigatório';
        if (formData.episodes.length === 0) newErrors.episodes = 'Selecione pelo menos um episódio';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const apiData = {
            id: initialData?.id,
            title: formData.title,
            subtitle: formData.subtitle,
            description: formData.description,
            thumbnailUrl: formData.thumbnailUrl,
            templateId: formData.templateId,
            additionalInfo: formData.additionalInfo,
            userAccess: formData.userAccess,
            episodes: formData.episodes
        };

        onSubmit(apiData);
        onClose();
    };

    // Filtra vídeos disponíveis para não mostrar os já selecionados
    const filteredVideos = availableVideos
        .filter(content =>
            (content.title || '').toLowerCase().includes(contentSearch.toLowerCase()) ||
            (content.description || '').toLowerCase().includes(contentSearch.toLowerCase())
        )
        .filter(content => !formData.episodes.some(ep => String(ep.content_id) === String(content.id)));

    const isVideoSelected = (videoId) =>
        formData.episodes.some(ep => ep.content_id === videoId);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="series-form-dialog"
        >
            <DialogTitle>
                {initialData ? 'Editar Série' : 'Nova Série'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box className="form-grid">
                        <TextField
                            name="title"
                            label="Título"
                            value={formData.title}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                        <TextField
                            name="subtitle"
                            label="Subtítulo"
                            value={formData.subtitle}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            name="description"
                            label="Descrição"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.description}
                            helperText={errors.description}
                        />
                        <TextField
                            name="thumbnailUrl"
                            label="URL da thumbnail"
                            value={formData.thumbnailUrl}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.thumbnailUrl}
                            helperText={errors.thumbnailUrl}
                        />
                        {/* Campo de seleção de template */}
                        <FormControl fullWidth margin="normal">
                            <InputLabel style={{ color: '#bdbdbd' }}>Template</InputLabel>
                            <Select
                                name="templateId"
                                value={formData.templateId || ''}
                                onChange={handleChange}
                                required
                                disabled={templates.length === 0}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            backgroundColor: '#232323',
                                            color: '#fff',
                                        },
                                    },
                                }}
                                sx={{
                                    backgroundColor: '#181818',
                                    color: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#444',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d32f2f',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d32f2f',
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: '#fff',
                                    },
                                }}
                            >
                                {templates.map((template) => (
                                    <MenuItem key={template.template_id} value={template.template_id} style={{ backgroundColor: '#232323', color: '#fff' }}>
                                        {template.template_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {templates.length === 0 && (
                                <div style={{ color: '#999', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Carregando templates...
                                </div>
                            )}
                        </FormControl>
                        {/* Fim campo de template */}
                        <Autocomplete
                            multiple
                            freeSolo={true}
                            options={['free', 'premium']}
                            value={formData.userAccess}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, userAccess: newValue });
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...rest } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={option}
                                            variant="outlined"
                                            label={option}
                                            sx={{
                                                backgroundColor: '#fff',
                                                color: '#222',
                                                borderColor: '#d32f2f',
                                                fontWeight: 600,
                                            }}
                                            {...rest}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Níveis de acesso" margin="normal" />
                            )}
                        />

                        <TextField
                            name="additionalInfo"
                            label="Informações adicionais (HTML)"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={4}
                        />

                        <Box className="videos-section">
                            <Typography variant="h6" className="section-title">
                                Vídeos da Série
                            </Typography>
                            {errors.episodes && (
                                <FormHelperText error>{errors.episodes}</FormHelperText>
                            )}
                            <TextField
                                label="Buscar Conteúdos"
                                value={contentSearch}
                                onChange={(e) => setContentSearch(e.target.value)}
                                fullWidth
                                margin="normal"
                            />

                            <Box className="videos-container">
                                <Box className="available-videos">
                                    <Typography variant="subtitle1">
                                        Vídeos Disponíveis ({filteredVideos.length})
                                    </Typography>
                                    <List className="video-list">
                                        {filteredVideos.map((content) => (
                                            <ListItem
                                                key={content.id}
                                                onClick={() => handleVideoClick(content)}
                                                selected={isVideoSelected(content.id)}
                                            >
                                                <Avatar
                                                    variant="rounded"
                                                    src={content.thumbnailUrl}
                                                    alt={content.title}
                                                    className="video-thumbnail"
                                                />
                                                <ListItemText
                                                    primary={content.title}
                                                    secondary={content.description}
                                                    className="video-text"
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Box className="selected-videos">
                                    <Typography variant="subtitle1">
                                        Vídeos Selecionados ({formData.episodes.length})
                                    </Typography>
                                    <List className="video-list">
                                        {formData.episodes
                                            .sort((a, b) => a.index - b.index)
                                            .map((episode, idx, arr) => {
                                                const content = availableVideos.find(v => String(v.id) === String(episode.content_id));
                                                if (!content) return null;
                                                return (
                                                    <ListItem key={episode.content_id}>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={content.thumbnailUrl}
                                                            alt={content.title}
                                                            className="video-thumbnail"
                                                        />
                                                        <ListItemText
                                                            primary={content.title}
                                                            secondary={`Episódio ${episode.index}`}
                                                            className="video-text"
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => moveEpisodeUp(idx)}
                                                                disabled={idx === 0}
                                                                style={{ color: idx === 0 ? '#666' : '#fff', marginRight: 4 }}
                                                                size="small"
                                                            >
                                                                <KeyboardArrowUp />
                                                            </IconButton>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => moveEpisodeDown(idx)}
                                                                disabled={idx === arr.length - 1}
                                                                style={{ color: idx === arr.length - 1 ? '#666' : '#fff', marginRight: 4 }}
                                                                size="small"
                                                            >
                                                                <KeyboardArrowDown />
                                                            </IconButton>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => handleVideoClick(content)}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                );
                                            })}
                                    </List>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {initialData ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SeriesForm; 